/**
 * Controls behaviour of the main site navigation.
 */
export default class Navigation {
  /**
   * @param {Boolean} waitForAssets - Should the navigation wait for assetsLoaded event before it becomes visible
   */
  constructor(waitForAssets) {
    this.navigation = this.getRootEl()
    this.navigationHeight = this.navigation.clientHeight
    this.langSwitch = this.setupLangSwitch()
    this.mobileNav = this.setupMobileNav()
    this.firstSection = this.getFirstSection()
    this.submenus = this.setupSubmenus()
    this.mobileNavOpen = false
    this.langSwitchOpen = false
    this.revealed = false
    this.frameTicking = false
    this.lastScrollPosition = 0
    this.scrollingUp = false
    this.scrollUpStart = null
    this.isPinned = false

    if (waitForAssets) {
      document.addEventListener('assetsLoaded', () => {
        this.reveal()
      }, { once: true })
    } else {
      this.reveal()
    }

    window.addEventListener('load', this.checkOverflows.bind(this))
  }

  getRootEl() {
    return document.querySelector('.navigation')
  }

  getFirstSection() {
    return document.querySelector('section')
  }

  checkOverflows() {
    this.navigation.querySelectorAll('.menu').forEach(it => {
      const hasOverflow = (it.scrollWidth - it.clientWidth) > 5
      hasOverflow && this.navigation.classList.add('navigation--compact')
    })
  }

  setupMobileNav() {
    const mobileNav = this.navigation.querySelector('.navigation__mobile')
    const hamburger = this.navigation.querySelector('.navigation__hamburger')

    hamburger.addEventListener('click', () => this.toggleMobileNav())

    return mobileNav
  }

  setupLangSwitch() {
    const langSwitch =  this.navigation.querySelector('.navigation__lang-switch')

    for (const item of langSwitch.children) {
      const isActiveLang = item.classList.contains('active')

      item.children[0].addEventListener('click', (e) => {
        if (isActiveLang) {
          e.preventDefault()
        }
        this.toggleLangSwitch()
      })
    }

    return langSwitch
  }

  setupSubmenus() {
    const submenus = this.navigation.querySelectorAll('.menu-item-has-children')

    submenus.forEach(it => {
      const trigger = it.querySelector(':scope > a')
      const submenu = it.querySelector(':scope > .sub-menu')

      if (!trigger || !submenu) {
        return
      }

      if (!submenu.id) {
        submenu.id = `navigation-submenu-${Math.random().toString(36).slice(2, 10)}`
      }

      const isToggleOnly = !trigger.hasAttribute('href')

      trigger.setAttribute('aria-controls', submenu.id)
      trigger.setAttribute('aria-expanded', 'false')
      trigger.setAttribute('aria-haspopup', 'true')

      if (isToggleOnly) {
        trigger.setAttribute('role', 'button')
        trigger.setAttribute('tabindex', '0')
      }

      trigger.addEventListener('click', e => {
        if (isToggleOnly) {
          e.preventDefault()
        }

        e.stopPropagation()
        this.toggleSubmenu(it)
      })

      trigger.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          e.preventDefault()
          this.closeSubmenu(it)
          trigger.focus()
          return
        }

        if (isToggleOnly && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          this.toggleSubmenu(it)
        }
      })

      it.addEventListener('focusout', e => {
        if (!it.contains(e.relatedTarget)) {
          this.closeSubmenu(it)
        }
      })
    })

    return submenus
  }

  setSubmenuState(item, isOpen) {
    const trigger = item.querySelector(':scope > a')

    item.classList.toggle('active', isOpen)
    trigger && trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
  }

  openSubmenu(item) {
    this.submenus.forEach(submenu => {
      if (submenu !== item) {
        this.closeSubmenu(submenu)
      }
    })

    this.setSubmenuState(item, true)
    clearTimeout(item.closeOnClickTimer)
    item.closeOnClickTimer = setTimeout(() => {
      document.addEventListener('click', item.closeOnClickListener = () => {
        this.closeSubmenu(item)
      })
    }, 50)
  }

  closeSubmenu(item) {
    clearTimeout(item.closeOnClickTimer)
    this.setSubmenuState(item, false)

    if (item.closeOnClickListener) {
      document.removeEventListener('click', item.closeOnClickListener)
      delete item.closeOnClickListener
    }
  }

  toggleSubmenu(item) {
    if (item.classList.contains('active')) {
      this.closeSubmenu(item)
    } else {
      this.openSubmenu(item)
    }
  }

  reveal() {
    this.navigation.classList.remove('hidden')
    this.revealed = true
    this.setScrollWatcher()
  }

  setScrollWatcher() {
    document.addEventListener('scroll', e => {
      this.scrollingUp = window.scrollY < this.lastScrollPosition

      if (!this.frameTicking) {
        window.requestAnimationFrame(() => {

          // reset if we reach top of the page
          if (window.scrollY <= 0) {
            this.unpin()
            this.navigation.classList.remove('navigation--solid')
          }

          if (this.scrollingUp) {
            // distance from point where scroll up started
            const delta = this.scrollUpStart - window.scrollY

            if (!this.scrollUpStart) {
              // store position when scroll up started to compute the delta
              this.scrollUpStart = window.scrollY
            } else if (
                !this.isPinned
                && delta > 250
                // don't pin when at the top section of page
                && window.scrollY > this.firstSection.clientHeight
              ) {
                this.pin()
            } else if (this.navigation.getBoundingClientRect().top > 0) {
              // pin without animation if navigation not fully out of viewbox
              this.pin(false)
            }
          } else if (this.isPinned) {
            this.unpin()
          }

          this.frameTicking = false
        })

        this.frameTicking = true
      }

      this.lastScrollPosition = window.scrollY
    })
  }

  pin(withAnimation = true) {
    this.navigation.classList.add('navigation--pinned', 'navigation--solid')
    this.navigation.style.animation = withAnimation
      ? 'navigationReveal 800ms cubic-bezier(0.08, 0.8, 0.3, 1) forwards'
      : 'none'
    this.isPinned = true
  }

  unpin() {
    this.navigation.classList.remove('navigation--pinned')
    this.navigation.style.animation = 'none'
    this.navigation.style.top = window.scrollY + 'px'
    this.scrollUpStart = null
    this.isPinned = false

    this.submenus.forEach(it => this.closeSubmenu(it))
  }

  toggleMobileNav() {
    if (this.mobileNavOpen) {
      this.mobileNav.classList.add('will-close')
      this.mobileNav.classList.remove('open')
      document.body.classList.remove('mobile-nav-open')

      setTimeout(() => {
        this.mobileNav.classList.remove('will-close')
      }, 500)

      this.mobileNavOpen = false
    } else {
      this.mobileNav.classList.add('will-open')
      
      setTimeout(() => {
        document.body.classList.add('mobile-nav-open')
        this.mobileNav.classList.add('open')
        this.mobileNav.classList.remove('will-open')
      }, 20);

      this.mobileNavOpen = true
    }
  }

  toggleLangSwitch() {
    if (this.langSwitchOpen) {
      this.langSwitch.classList.remove('open')
      this.langSwitchOpen = false
    } else {
      this.langSwitch.classList.add('open')
      this.langSwitchOpen = true
    }
  }
}
